import { views, eventMap, componentClasses } from "../../../core/components/file-input/file-input";
import { renderState, dispatchEvent } from "../../../core/js/state-machine";
import { fileInputItemTemplate } from "./file-input-item.template";
import FileInputItem from "./file-input-item";
import { alertCheckCir, alertNotice } from "../../../core/js/helpers/built-in-icons";
import { randomNumGenerator } from "../../../core/js/utilities";
import { actions } from "../../../core/js/state-machine/reducers/file-input";

/**
 * Initialize the FileInputList component
 * Used for managing a list of file items for the file input component.
 * @param {HTMLElement} element main File Input List wrapper
 * @param {defaultProps} props optional settings.
 * @returns {{
 *  element: HTMLElement,
 *  add: Function,
 *  remove: Function,
 *  setState: Function,
 * }} public api object
 */
const FileInputList = (element, props = {}) => {
    // Keep code convention of using mergedProps
    const mergedProps = props;

    /**
     * When we add our tracked list of fileInputItems, we make a new fileInputItem component.
     * @param {File} file
     * @param {HTMLElement} fileInputItemElement
     * @returns {HTMLElement} created fileInputItem
     */
    const addFileInputItem = (file, fileInputItemElement) => {
        // Our files are associated with a unique ID for manipulating the list of items.

        const fileInputItem = FileInputItem(fileInputItemElement, {
            id: `file-item-${randomNumGenerator()}`,
            file,
            fileInputStore: mergedProps.fileInputStore,
            srFileRemovedDescription: mergedProps.srFileRemovedDescription,
        });
        return fileInputItem;
    };

    /**
     * Create the HTML structure and element for a fileInputItem and append it to the our list in the dom.
     * @param {File} file
     * @returns
     */
    const buildFileInputItemElement = (file) => {
        const id = `file-item-${randomNumGenerator()}`;
        const fileInputItemElement = new DOMParser().parseFromString(
            fileInputItemTemplate(file, id),
            "text/html"
        ).body.firstChild;

        element.appendChild(fileInputItemElement);

        return fileInputItemElement;
    };

    /**
     * Take a fileInputItem and make sure it passes all of the required validation before attaching
     * it to our control.
     * @param {FileInputItem} fileInputItem
     */
    const validateFileItem = (fileInputItem) => {
        exceedsMaxFileSize(fileInputItem) || exceedsMaxFileCount(fileInputItem);
    };

    /**
     * Handle adding a new File object to the list of fileItems.
     * @param {File} file
     * @returns
     */
    const add = (file) => {
        if (!(file instanceof File)) {
            console.error(`FileInput.add() must only be used with File objects. Received: ${file}`);
            return;
        }

        const fileInputItemElement = buildFileInputItemElement(file);

        const fileInputItem = addFileInputItem(file, fileInputItemElement);

        mergedProps.fileInputStore.dispatch(actions.addListItem(fileInputItem));
        dispatchEvent(eventMap.itemAdded, fileInputItemElement);

        validateFileItem(fileInputItem);
    };

    /**
     * Verify that our file meets the maxFileSize requirement.
     * @param {FileInputItem} fileInputItem
     * @returns {boolean} whether or not there is an error.
     */
    const exceedsMaxFileSize = (fileInputItem) => {
        if (fileInputItem.file.size > mergedProps.maxFileSize) {
            renderState(views.item.error, fileInputItem.element);
            mergedProps.fileInputStore.dispatch(
                actions.setListItemError(
                    fileInputItem.id,
                    `${alertNotice({ class: `${componentClasses.item.feedbackIcon}` })} ${
                        mergedProps.errorMaxFileSize
                    }`
                )
            );
            // Update the field error which is our general error since there is now a file with an error on it.
            mergedProps.fileInputStore.dispatch(actions.setFieldError(true));
            return true;
        }

        return false;
    };

    /**
     * Verify the number of valid files we have in our fileInputItems list.
     * Because we can assume that this validation is only called on synchronous adding of files. We
     * can (reasonably) safely expect a consistent order of the fileInputItems array.
     * @param {FileInputItem} fileInputItem
     * @returns {boolean} whether or not there is an error.
     */
    const exceedsMaxFileCount = (fileInputItem) => {
        // Consider all files.
        const { fullItemCount } = mergedProps.fileInputStore.getState();

        if (fullItemCount > mergedProps.maxFileCount) {
            renderState(views.item.error, fileInputItem.element);
            mergedProps.fileInputStore.dispatch(
                actions.setListItemError(
                    fileInputItem.id,
                    `${alertNotice({ class: `${componentClasses.item.feedbackIcon}` })} ${
                        mergedProps.errorMaxFileCount
                    }`
                )
            );
            // Update the field error which is our general error since now there is a file with an error on it.
            mergedProps.fileInputStore.dispatch(actions.setFieldError(true));
            return true;
        }

        return false;
    };

    /**
     * Provide a way to programmatically manipulate the items of the list.
     * @param {number} index
     * @returns
     */
    const remove = (index) => {
        const { list } = mergedProps.fileInputStore.getState();

        const fileItem = list[index];

        if (!fileItem) return;

        list[index].remove();
    };

    /**
     * When we want to change the items state externally, like as a response from the API, we can
     * use this method to pass the state.
     * @param {number} index
     * @param {string} state
     * @param {string} message
     * @returns
     */
    const setState = (index, state, message) => {
        const states = ["success", "uploading", "error", "rest"];
        if (!states.includes(state)) return;

        const { list } = mergedProps.fileInputStore.getState();
        const fileItem = list[index];
        if (!fileItem) return;

        if (message) {
            fileItem.setFeedback(message);
        }

        renderState(views.item[state], fileItem.element);
        if (state === "success") {
            fileItem.setFeedback(
                `${alertCheckCir({ class: `${componentClasses.item.feedbackIcon}` })} ${
                    mergedProps.successMessage
                }`
            );
            setTimeout(() => {
                renderState(views.item.rest, fileItem.element);
            }, 2000);
        }
    };

    return {
        element,
        add,
        remove,
        setState,
    };
};

export default FileInputList;
